import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { ClientDashboardStats } from '../../types';

const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ClientDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    const response = await api.getClientDashboard();
    if (response.success && response.data) {
      setStats(response.data);
    } else {
      setError(response.error || 'Failed to load dashboard');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={loadDashboard} className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const usagePercentToday = stats ? (stats.usageToday / stats.dailyLimit) * 100 : 0;
  const usagePercentMonth = stats ? (stats.usageThisMonth / stats.monthlyLimit) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Welcome, {user?.firstName || 'Client'}!</h1>
          <p className="text-neutral-500">Your GEO-PRO dashboard overview</p>
        </div>
        <Link
          to="/client/engine"
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
        >
          <span>🚀</span>
          Launch Engine
        </Link>
      </div>

      {/* Subscription Status */}
      {stats?.subscription && stats?.plan && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💎</span>
              </div>
              <div>
                <p className="text-neutral-400 text-sm">Current Plan</p>
                <p className="text-xl font-bold text-white">{stats.plan.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-neutral-400 text-sm">Expires</p>
                <p className="text-white font-medium">
                  {new Date(stats.subscription.expiresAt).toLocaleDateString('en-BD', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className={`px-4 py-2 rounded-xl ${
                stats.daysRemaining > 7
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : stats.daysRemaining > 0
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-red-500/20 text-red-400'
              }`}>
                <span className="font-bold">{stats.daysRemaining}</span>
                <span className="text-sm ml-1">days left</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Today's Usage */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <p className="text-neutral-400 text-sm">Today's Usage</p>
                <p className="text-2xl font-bold text-white">
                  {stats?.usageToday || 0} <span className="text-neutral-500 text-lg">/ {stats?.dailyLimit || 0}</span>
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
              usagePercentToday >= 90
                ? 'bg-red-500/20 text-red-400'
                : usagePercentToday >= 70
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              {usagePercentToday.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-[#262626] rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                usagePercentToday >= 90
                  ? 'bg-red-500'
                  : usagePercentToday >= 70
                    ? 'bg-yellow-500'
                    : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(usagePercentToday, 100)}%` }}
            />
          </div>
          <p className="text-neutral-500 text-xs mt-2">
            {stats?.dailyLimit ? stats.dailyLimit - (stats?.usageToday || 0) : 0} requests remaining today
          </p>
        </div>

        {/* Monthly Usage */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📈</span>
              <div>
                <p className="text-neutral-400 text-sm">This Month</p>
                <p className="text-2xl font-bold text-white">
                  {stats?.usageThisMonth || 0} <span className="text-neutral-500 text-lg">/ {stats?.monthlyLimit || 0}</span>
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
              usagePercentMonth >= 90
                ? 'bg-red-500/20 text-red-400'
                : usagePercentMonth >= 70
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              {usagePercentMonth.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-[#262626] rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                usagePercentMonth >= 90
                  ? 'bg-red-500'
                  : usagePercentMonth >= 70
                    ? 'bg-yellow-500'
                    : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(usagePercentMonth, 100)}%` }}
            />
          </div>
          <p className="text-neutral-500 text-xs mt-2">
            {stats?.monthlyLimit ? stats.monthlyLimit - (stats?.usageThisMonth || 0) : 0} requests remaining this month
          </p>
        </div>
      </div>

      {/* Quick Actions & Features */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Available Features */}
        <div className="lg:col-span-2 bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Available Features</h3>
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">
              {stats?.availableFeatures?.length || 0} MODES
            </span>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {stats?.availableFeatures?.map((feature, idx) => (
              <Link
                key={idx}
                to="/client/engine"
                className="p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] hover:border-emerald-500/30 transition-all group"
              >
                <p className="text-white font-medium text-sm group-hover:text-emerald-400 transition-colors">
                  {feature}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626]">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Quick Actions</h3>
          </div>
          <div className="p-4 space-y-3">
            <Link
              to="/client/engine"
              className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/20 transition-colors group"
            >
              <span className="text-xl">🚀</span>
              <div className="flex-1">
                <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Start Engine</p>
                <p className="text-neutral-500 text-xs">Generate optimized content</p>
              </div>
              <span className="text-neutral-500 group-hover:text-emerald-400 transition-colors">→</span>
            </Link>

            <Link
              to="/client/usage"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">📊</span>
              <div className="flex-1">
                <p className="text-white font-medium">Usage History</p>
                <p className="text-neutral-500 text-xs">View past requests</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>

            <Link
              to="/client/subscription"
              className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
            >
              <span className="text-xl">💳</span>
              <div className="flex-1">
                <p className="text-white font-medium">Subscription</p>
                <p className="text-neutral-500 text-xs">Manage your plan</p>
              </div>
              <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Usage */}
      {stats?.recentUsage && stats.recentUsage.length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Recent Activity</h3>
            <Link to="/client/usage" className="text-emerald-500 text-xs hover:text-emerald-400 transition-colors">
              View All →
            </Link>
          </div>
          <div className="divide-y divide-[#262626]">
            {stats.recentUsage.slice(0, 5).map((usage) => (
              <div key={usage.id} className="p-4 hover:bg-[#1a1a1a] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${usage.success ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <div>
                      <p className="text-white font-medium">{usage.featureCode}</p>
                      <p className="text-neutral-500 text-xs">
                        {new Date(usage.createdAt).toLocaleString('en-BD')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-neutral-400 text-sm">{usage.processingTimeMs}ms</p>
                    {usage.requestTokens > 0 && (
                      <p className="text-neutral-500 text-xs">{usage.requestTokens + usage.responseTokens} tokens</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
